"use client";
import { useState, useEffect, useRef } from "react";

/* ─── Data ──────────────────────────────────────────────────────── */

const VW = 380, VH = 270;

const NODES = [
  { id: 0, x: 72,  y: 55,  c: "#049FD9", rgb: "4,159,217",   fk: "nf-fl-a", fd: "4.2s", pd: "0.0s" },
  { id: 1, x: 195, y: 36,  c: "#22d3ee", rgb: "34,211,238",  fk: "nf-fl-b", fd: "5.1s", pd: "0.5s" },
  { id: 2, x: 310, y: 70,  c: "#a78bfa", rgb: "167,139,250", fk: "nf-fl-c", fd: "4.7s", pd: "1.0s" },
  { id: 3, x: 42,  y: 155, c: "#22d3ee", rgb: "34,211,238",  fk: "nf-fl-b", fd: "5.8s", pd: "1.5s" },
  { id: 4, x: 158, y: 138, c: "#049FD9", rgb: "4,159,217",   fk: "nf-fl-a", fd: "4.5s", pd: "0.3s" },
  { id: 5, x: 258, y: 128, c: "#a78bfa", rgb: "167,139,250", fk: "nf-fl-c", fd: "5.3s", pd: "1.2s" },
  { id: 6, x: 336, y: 173, c: "#34d399", rgb: "52,211,153",  fk: "nf-fl-b", fd: "4.9s", pd: "0.8s" },
  { id: 7, x: 95,  y: 232, c: "#34d399", rgb: "52,211,153",  fk: "nf-fl-a", fd: "5.5s", pd: "1.8s" },
  { id: 8, x: 216, y: 244, c: "#049FD9", rgb: "4,159,217",   fk: "nf-fl-c", fd: "4.3s", pd: "0.6s" },
  { id: 9, x: 314, y: 222, c: "#22d3ee", rgb: "34,211,238",  fk: "nf-fl-a", fd: "5.7s", pd: "1.4s" },
];

const EDGES = [
  { a: 0, b: 1, ph: 1, del: 0.0, sd: "3.8s", sOff: "0.0s" },
  { a: 1, b: 4, ph: 1, del: 0.8, sd: "3.0s", sOff: "0.6s" },
  { a: 3, b: 4, ph: 1, del: 1.5, sd: "3.4s", sOff: "1.2s" },
  { a: 1, b: 2, ph: 2, del: 0.0, sd: "2.8s", sOff: "0.3s" },
  { a: 4, b: 5, ph: 2, del: 0.7, sd: "3.2s", sOff: "1.0s" },
  { a: 0, b: 3, ph: 2, del: 1.4, sd: "4.0s", sOff: "1.8s" },
  { a: 5, b: 6, ph: 2, del: 2.0, sd: "2.5s", sOff: "0.7s" },
  { a: 4, b: 8, ph: 2, del: 2.6, sd: "3.5s", sOff: "1.5s" },
  { a: 2, b: 5, ph: 3, del: 0.0, sd: "3.0s", sOff: "0.0s" },
  { a: 7, b: 8, ph: 3, del: 0.6, sd: "3.6s", sOff: "0.8s" },
  { a: 8, b: 9, ph: 3, del: 1.2, sd: "2.8s", sOff: "1.6s" },
  { a: 5, b: 9, ph: 3, del: 1.8, sd: "3.2s", sOff: "0.5s" },
  { a: 6, b: 9, ph: 3, del: 2.4, sd: "3.8s", sOff: "1.2s" },
  { a: 3, b: 7, ph: 3, del: 3.0, sd: "3.0s", sOff: "0.9s" },
  { a: 1, b: 5, ph: 3, del: 3.6, sd: "3.4s", sOff: "2.0s" },
];

const PHASE_TIMES = [0, 5200, 8800, 12800, 17500];

const PHASE_LABELS = [
  "Initializing agents",
  "Awareness forming",
  "Connections spreading",
  "Synchronizing",
  "Shared reasoning network",
];

/* ─── Inner (one cycle) ──────────────────────────────────────────── */

function NetworkFormationInner({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase]             = useState(0);
  const [visNodes, setVisNodes]       = useState(new Set<number>());
  const [activeEdges, setActiveEdges] = useState(new Set<number>());
  const [signalEdges, setSignalEdges] = useState(new Set<number>());
  const [fadingOut, setFadingOut]     = useState(false);
  const scheduledEdges = useRef(new Set<number>());
  const signalsStarted = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  /* Phase clock -------------------------------------------------- */
  useEffect(() => {
    const push = (fn: () => void, ms: number) => {
      const t = setTimeout(fn, ms);
      timers.current.push(t);
    };
    push(() => setPhase(1), PHASE_TIMES[1]!);
    push(() => setPhase(2), PHASE_TIMES[2]!);
    push(() => setPhase(3), PHASE_TIMES[3]!);
    push(() => setPhase(4), PHASE_TIMES[4]!);
    // Show stable state for 2.8 s then fade out and complete
    push(() => setFadingOut(true), PHASE_TIMES[4]! + 2800);
    push(() => onComplete(),       PHASE_TIMES[4]! + 3600);
    return () => timers.current.forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Nodes: staggered appearance ---------------------------------- */
  useEffect(() => {
    NODES.forEach((_, i) => {
      const t = setTimeout(
        () => setVisNodes(prev => new Set([...prev, i])),
        500 + i * 420,
      );
      timers.current.push(t);
    });
  }, []);

  /* Edges: activate once phase unlocks them ---------------------- */
  useEffect(() => {
    EDGES.forEach((e, i) => {
      if (e.ph <= phase && !scheduledEdges.current.has(i)) {
        scheduledEdges.current.add(i);
        const delay = Math.max(0, e.del * 1000);
        const t = setTimeout(
          () => setActiveEdges(prev => new Set([...prev, i])),
          delay,
        );
        timers.current.push(t);
      }
    });
  }, [phase]);

  /* Signal pulses: start once phase 3 is reached ---------------- */
  useEffect(() => {
    if (phase < 3 || signalsStarted.current) return;
    signalsStarted.current = true;
    EDGES.forEach((_, i) => {
      const t = setTimeout(
        () => setSignalEdges(prev => new Set([...prev, i])),
        i * 280,
      );
      timers.current.push(t);
    });
  }, [phase]);

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <>
      <style>{`
        @keyframes nf-fl-a { 0%,100%{ transform:translateY(0px) } 50%{ transform:translateY(-4px) } }
        @keyframes nf-fl-b { 0%,100%{ transform:translateY(0px) } 50%{ transform:translateY(-3px) } }
        @keyframes nf-fl-c { 0%,100%{ transform:translateY(0px) } 50%{ transform:translateY(-5px) } }
        @keyframes nf-label-in {
          from { opacity:0; transform:translateY(4px); }
          to   { opacity:1; transform:translateY(0);   }
        }
        @keyframes nf-node-in {
          0%   { opacity:0; }
          100% { opacity:1; }
        }
      `}</style>

      <div style={{
        width: "100%", height: "100%", minHeight: 400,
        background: "linear-gradient(160deg, #05070E 0%, #080A16 55%, #060810 100%)",
        fontFamily: "'Inter', system-ui, sans-serif",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "14px 12px 12px 6px", boxSizing: "border-box",
        position: "relative", overflow: "hidden",
        opacity: fadingOut ? 0 : 1,
        transition: "opacity 0.8s ease",
      }}>
        {/* Background radial */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "radial-gradient(ellipse 65% 55% at 50% 50%, rgba(4,159,217,0.07) 0%, rgba(167,139,250,0.04) 50%, transparent 80%)",
        }} />

        {/* Phase label */}
        <div key={phase} style={{ marginBottom: 10, height: 18, display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{
            width: 5, height: 5, borderRadius: "50%", background: "#049FD9",
            boxShadow: "0 0 6px 2px rgba(4,159,217,0.6)",
          }} />
          <span style={{
            fontSize: 9, color: "rgba(4,159,217,0.80)", fontWeight: 600,
            letterSpacing: "0.10em", textTransform: "uppercase",
            animation: "nf-label-in 0.45s ease forwards",
          }}>
            {PHASE_LABELS[phase]}
          </span>
        </div>

        {/* SVG canvas */}
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          style={{ width: "100%", maxWidth: 400, flex: 1, overflow: "visible" }}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* ── Edges  -  only rendered once activated ── */}
          {EDGES.map((e, i) => {
            if (!activeEdges.has(i)) return null;
            const na = NODES[e.a]!, nb = NODES[e.b]!;
            const len = Math.sqrt((nb.x - na.x) ** 2 + (nb.y - na.y) ** 2);
            return (
              <line key={`edge-${i}`}
                x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                stroke={na.c} strokeWidth={0.9} strokeOpacity={0}
                strokeDasharray={len} strokeDashoffset={len}
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from={len} to={0}
                  dur="1.3s" fill="freeze"
                  calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1"
                />
                <animate
                  attributeName="stroke-opacity"
                  from={0} to={0.40}
                  dur="0.8s" fill="freeze"
                />
              </line>
            );
          })}

          {/* ── Signal pulses ── */}
          {EDGES.map((e, i) => {
            if (!signalEdges.has(i)) return null;
            const na = NODES[e.a]!, nb = NODES[e.b]!;
            const fwdPath = `M${na.x},${na.y} L${nb.x},${nb.y}`;
            const bwdPath = `M${nb.x},${nb.y} L${na.x},${na.y}`;
            return (
              <g key={`sig-${i}`}>
                <circle r={2.0} fill={na.c} fillOpacity={0.85}>
                  <animateMotion dur={e.sd} repeatCount="indefinite" begin={e.sOff} path={fwdPath} />
                </circle>
                {i % 3 === 1 && (
                  <circle r={1.5} fill={nb.c} fillOpacity={0.55}>
                    <animateMotion dur={e.sd} repeatCount="indefinite" begin={e.sOff} path={bwdPath} />
                  </circle>
                )}
              </g>
            );
          })}

          {/* ── Nodes ── */}
          {NODES.map((n, i) => {
            if (!visNodes.has(i)) return null;
            const isSync   = phase >= 3;
            const isStable = phase >= 4;
            return (
              <g key={`node-${n.id}`}
                style={{
                  animation: `nf-fl-${n.fk.split("-")[2]} ${n.fd} ease-in-out ${n.pd} infinite`,
                  transformOrigin: `${n.x}px ${n.y}px`,
                  transformBox: "fill-box",
                }}
              >
                <g style={{ animation: "nf-node-in 1.8s ease forwards" }}>
                  <circle cx={n.x} cy={n.y} r={isSync ? 13 : 9}
                    fill={n.c} fillOpacity={isSync ? 0.11 : 0.05}
                    style={{ transition: "r 0.9s ease, fill-opacity 0.9s ease" }}
                  />
                  {phase >= 1 && (
                    <circle cx={n.x} cy={n.y} r={5} fill="none"
                      stroke={n.c} strokeWidth={1.1}>
                      <animate attributeName="r" from={5} to={15}
                        dur={n.fd} begin={n.pd} repeatCount="indefinite" />
                      <animate attributeName="stroke-opacity" from={0.55} to={0}
                        dur={n.fd} begin={n.pd} repeatCount="indefinite" />
                    </circle>
                  )}
                  <circle cx={n.x} cy={n.y} r={5} fill={n.c}
                    style={{
                      filter: isSync ? `drop-shadow(0 0 5px ${n.c})` : "none",
                      transition: "filter 1s ease",
                    }}
                  >
                    {isSync && (
                      <animate attributeName="fill-opacity" from={0.75} to={1}
                        dur={n.fd} begin={n.pd} repeatCount="indefinite" />
                    )}
                  </circle>
                  <circle cx={n.x} cy={n.y} r={1.8} fill="#fff"
                    fillOpacity={isStable ? 0.60 : isSync ? 0.45 : 0.28}
                    style={{ transition: "fill-opacity 0.9s ease" }}
                  />
                </g>
              </g>
            );
          })}
        </svg>

        {/* Progress bar */}
        <div style={{
          marginTop: 10, width: "100%", maxWidth: 360,
          height: 2, borderRadius: 999,
          background: "rgba(255,255,255,0.06)", position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", left: 0, top: 0, bottom: 0, borderRadius: 999,
            background: "linear-gradient(to right, #049FD9, #a78bfa)",
            width: `${(phase / 4) * 100}%`,
            transition: "width 1.4s cubic-bezier(0.4,0,0.2,1)",
          }} />
        </div>

        {/* Phase step dots */}
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          {[0,1,2,3,4].map(p => (
            <div key={p} style={{
              width: p === phase ? 18 : 4, height: 4, borderRadius: 999,
              background: p <= phase ? "#049FD9" : "rgba(255,255,255,0.12)",
              transition: "width 0.4s ease, background 0.4s ease",
            }} />
          ))}
        </div>
      </div>
    </>
  );
}

/* ─── Looping wrapper ────────────────────────────────────────────── */

export function NetworkFormation() {
  const [cycle, setCycle] = useState(0);
  return (
    <NetworkFormationInner
      key={cycle}
      onComplete={() => setCycle(c => c + 1)}
    />
  );
}
